import { Calendar, RefreshCw } from "lucide-react";
import { formatCurrency, formatNumber, parseBigFloat } from "@west-tennessee-consulting/ui";
import type { Recurring_Transactions } from "@/src/gql/graphql";
import type { DetailHeaderModel, HeaderFact, HeaderMetric, HeaderSubtitleSegment } from "../model";

/** Guarded against GetRecurringTransaction by detail-header-model-coverage.test.ts. */
export const RECURRING_TRANSACTION_HEADER_FIELDS = [
  "item",
  "amount",
  "quantity",
  "company_id",
  "company_name",
  "sync_status",
  "last_synced_month",
  "quickbooks_id",
] as const satisfies readonly (keyof Recurring_Transactions)[];

export type RecurringTransactionHeaderData = Pick<
  Recurring_Transactions,
  (typeof RECURRING_TRANSACTION_HEADER_FIELDS)[number]
>;

/** 1:1 scalar columns edited in the header (dropped from the inline panel). */
export const RECURRING_TRANSACTION_HEADER_EDITABLE_FIELDS = [
  "amount",
  "quantity",
] as const satisfies readonly (keyof Recurring_Transactions)[];
type RecurringTransactionHeaderEditableField =
  (typeof RECURRING_TRANSACTION_HEADER_EDITABLE_FIELDS)[number];

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function buildRecurringTransactionHeaderModel(
  transaction: RecurringTransactionHeaderData
): DetailHeaderModel {
  const subtitleSegments: HeaderSubtitleSegment[] = [];
  if (transaction.item) subtitleSegments.push({ text: transaction.item });
  if (transaction.company_name) {
    subtitleSegments.push({
      text: transaction.company_name,
      href: transaction.company_id ? `/companies/${transaction.company_id}` : undefined,
    });
  }

  const facts: HeaderFact[] = [];
  if (transaction.sync_status) {
    const invoiceSuffix = transaction.quickbooks_id ? ` · Invoice ${transaction.quickbooks_id}` : "";
    facts.push({
      id: "qb-sync",
      icon: RefreshCw,
      label: "QB sync",
      value: `${capitalize(transaction.sync_status)}${invoiceSuffix}`,
    });
  }
  if (transaction.last_synced_month) {
    facts.push({
      id: "last-synced-month",
      icon: Calendar,
      label: "Last synced",
      value: transaction.last_synced_month,
    });
  }

  const metrics: HeaderMetric[] = [];
  const amount = formatCurrency(transaction.amount);
  if (amount) {
    metrics.push({
      id: "amount",
      label: "Amount",
      value: amount,
      fieldId: "amount" satisfies RecurringTransactionHeaderEditableField,
    });
  }

  const quantity = formatNumber(transaction.quantity);
  if (quantity) {
    metrics.push({
      id: "quantity",
      label: "Qty",
      value: quantity,
      fieldId: "quantity" satisfies RecurringTransactionHeaderEditableField,
    });
  }

  const amountNum = parseBigFloat(transaction.amount);
  const quantityNum = parseBigFloat(transaction.quantity);
  if (amountNum !== null && quantityNum !== null) {
    const total = formatCurrency(amountNum * quantityNum);
    if (total) metrics.push({ id: "total", label: "Total", value: total, hint: "/mo" });
  }

  return { subtitleSegments, facts, metrics };
}
